import React, { useEffect, useState } from 'react';
import { TextField, Button, Grid2, Alert, FormControl, Autocomplete, Checkbox, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reportServices } from '../../services/reportServices';
import { jobServices } from '../../services/jobServices';
import { useAuth } from '../../services/authentication/authContext';
import Clear from '@mui/icons-material/Clear';
import GetApp from '@mui/icons-material/GetApp';
import { downloadReport } from '../../utils/downloadReport ';
import '../../css/Reports.css';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import ReportGenerateButton from '../common/ReportGenerateButton';
import PageHeader from '../ui/PageHeader';

const ClientWiseJobReport = ({ showHeader = true }) => {

    const [clientList, setClientList] = useState([]);
    const [selectedClientIds, setSelectedClientIds] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState('');
    const [generating, setGenerating] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const { accessToken } = useAuth();

    useEffect(() => {
        if (accessToken) {
            setLoading(true);
            jobServices.getClientsDataInJob()
                .then((response) => {
                    setClientList(response);
                    setLoading(false);
                })
                .catch((error) => {
                    setLoading(false);
                });
        }
    }, [accessToken]);

    const handleClear = () => {
        setStartDate('');
        setEndDate('');
        setSelectedClientIds([]);
    };

    const handleReport = () => {
        setGenerating(true);
        const formData = {
            startDate,
            endDate,
            clientIds: selectedClientIds
        };
        if (startDate <= endDate) {
            reportServices.downloadClientWiseJobReport(formData)
                .then((response) => {

                    downloadReport(response);

                    setGenerating(false);
                    setSuccessMessage("Report Generated Successfully!");
                    setTimeout(() => setSuccessMessage(null), 4000);

                    setSelectedClientIds([]);
                    setEndDate('');
                    setStartDate('');
                })
                .catch((error) => {
                    setError(error.message);
                    setTimeout(() => {
                        setError(null);
                    }, 5000);
                    setGenerating(false);
                });
        } else {
            setError('Error! Start date must be earlier than end date.');
            setTimeout(() => {
                setError(null);
            }, 5000);
            setGenerating(false);
        }
    };

    return (
        <div className={showHeader ? "reports-container" : ""}>
            {showHeader && (
                <PageHeader title="Client Wise Job Report" />
            )}
            <Box className={showHeader ? "report-box" : ""}>

                {successMessage && (
                    <Alert variant="filled" severity="success" style={{ marginBottom: '1.25rem' }}>
                        {successMessage}
                    </Alert>
                )}

                {error && (
                    <Alert variant="outlined" severity="error" style={{ marginBottom: '1.25rem' }}>
                        {error}
                    </Alert>
                )}

                <form style={{ marginTop: '1.25rem' }}>
                    <Grid2 container spacing={3}>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <TextField
                                type="date"
                                label={t('report.startDate')}
                                variant="outlined"
                                fullWidth
                                required
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                margin="normal"
                                slotProps={{
                                    inputLabel: { shrink: true },
                                }}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <TextField
                                type="date"
                                label={t('report.endDate')}
                                variant="outlined"
                                fullWidth
                                required
                                value={endDate}
                                disabled={startDate ? false : true}
                                onChange={(e) => setEndDate(e.target.value)}
                                margin="normal"
                                slotProps={{
                                    inputLabel: { shrink: true },
                                }}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth margin="normal">
                                <Autocomplete
                                    multiple
                                    id="multi-client-checkbox"
                                    options={[{ id: 'all', firstName: 'Select All' }, ...clientList]}
                                    disableCloseOnSelect
                                    value={clientList.filter(client => selectedClientIds.includes(client.id))}
                                    getOptionLabel={(client) =>
                                        client.id === 'all'
                                            ? 'Select All'
                                            : `${client.name}`
                                    }
                                    onChange={(event, newValue) => {
                                        const isSelectAllClicked = newValue.some(client => client.id === 'all');

                                        if (isSelectAllClicked) {
                                            // Add all clients to selection (ignore duplicates)
                                            const allClientIds = clientList.map(client => client.id);
                                            const isAllSelected = selectedClientIds.length === clientList.length;

                                            if (isAllSelected) {
                                                setSelectedClientIds([]);
                                            } else {
                                                setSelectedClientIds(allClientIds);
                                            }
                                        } else {
                                            const filtered = newValue.filter(client => client.id !== 'all');
                                            setSelectedClientIds(filtered.map((client) => client.id));
                                        }
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option, { selected }) => {
                                        const isAllSelected = selectedClientIds.length === clientList.length;

                                        return (
                                            <li {...props}>
                                                <Checkbox
                                                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                                                    checkedIcon={<CheckBox fontSize="small" />}
                                                    style={{ marginRight: 8 }}
                                                    checked={option.id === 'all' ? isAllSelected : selected}
                                                />
                                                {option.id === 'all'
                                                    ? 'Select All'
                                                    : `${option.name}`}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Client"
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid2>

                        <Grid2 size={{ xs: 12 }} className="gridContainer">
                            <ReportGenerateButton
                                onClick={handleReport}
                                loading={generating}
                                disabled={!(startDate && endDate && selectedClientIds.length > 0) || (Boolean(loading) || generating)}
                                className={`commonButton ${(startDate && endDate && selectedClientIds.length > 0) ? 'reportButtonSuccess' : 'reportButtonDefault'}`}
                                icon={<GetApp className="buttonIcon" />}
                                label={t('job.report')}
                                loadingLabel="Preparing your report..."
                            />
                            <Button
                                type="button"
                                variant="contained"
                                onClick={handleClear}
                                className={`commonButton clearButton`}
                                disabled={(Boolean(loading) || generating)}
                            >
                                <Clear className="buttonIcon" /> {t('job.clear')}
                            </Button>
                        </Grid2>

                    </Grid2>
                </form>
            </Box>
        </div>
    );
};

export default ClientWiseJobReport;
